import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/userSlice";
import { useNavigate } from "react-router-dom";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false);
  const [isMobileMenuDropdownOpen, setIsMobileMenuDropdownOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileMenuDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.navbar-menu-dropdown')) {
        setIsMenuDropdownOpen(false);
      }
    };

    if (isMenuDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMenuDropdownOpen]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleMenuDropdown = () => {
    setIsMenuDropdownOpen(!isMenuDropdownOpen);
  };

  const toggleMobileMenuDropdown = () => {
    setIsMobileMenuDropdownOpen(!isMobileMenuDropdownOpen);
  };

  if (!isAuthenticated) {
    return null;
  }

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/daily-work-entry", label: "Daily Work Entry" },
    { path: "/bill", label: "Bill" },
    { path: "/payment", label: "Payment" },
  ];

  const menuDropdownLinks = [
    { path: "/process", label: "Process" },
    { path: "/party", label: "Party" },
    { path: "/emp", label: "Employee" },
    { path: "/empwork", label: "Emp Work" },
    { path: "/bill-report", label: "Bill Report" },
    { path: "/emp-report", label: "Emp Report" },
    { path: "/work-report", label: "Work Report" },
    { path: "/outstanding-payments", label: "Outstanding Payments" },
  ];
  

  return (
    <>
      <style>{`
        /* ============================================
           NAVBAR BASE STYLES - GLOBAL
           ============================================ */
        * {
          -webkit-tap-highlight-color: transparent;
        }

        .navbar,
        .navbar *,
        .navbar-container,
        .navbar-content,
        .navbar-user-section,
        .navbar-menu {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .navbar::-webkit-scrollbar,
        .navbar-container::-webkit-scrollbar,
        .navbar-content::-webkit-scrollbar,
        .navbar-user-section::-webkit-scrollbar,
        .navbar-menu::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }

        /* ============================================
           NAVBAR CONTAINER - BASE
           ============================================ */
        .navbar {
          background: #ffffff;
          box-shadow: 0 2px 10px rgba(30, 64, 175, 0.1);
          position: sticky;
          top: 0;
          z-index: 1000;
          border-bottom: 2px solid #e0e7ff;
          width: 100%;
          overflow: visible;
        }

        .navbar-container {
          max-width: 1600px;
          margin: 0 auto;
          padding: 0 20px;
          width: 100%;
          overflow: visible;
        }

        .navbar-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 70px;
          width: 100%;
          overflow: visible;
        }

        /* ============================================
           NAVBAR BRAND - LOGO
           ============================================ */
        .navbar-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #1e40af;
          font-size: 1.5rem;
          font-weight: 700;
          text-decoration: none;
          cursor: pointer;
          transition: transform 0.2s ease;
          letter-spacing: -0.5px;
        }

        .navbar-brand:hover {
          transform: scale(1.05);
          color: #1e3a8a;
        }

        /* ============================================
           DESKTOP MENU - ALWAYS VISIBLE ON DESKTOP
           ============================================ */
        .navbar-menu {
          display: flex;
          align-items: center;
          gap: 8px;
          list-style: none;
          margin: 0;
          padding: 0;
          overflow: visible;
        }

        .navbar-link {
          padding: 10px 16px;
          color: #1e293b;
          text-decoration: none;
          font-weight: 500;
          border-radius: 8px;
          transition: all 0.3s ease;
          position: relative;
          display: inline-block;
          border: 2px solid transparent;
        }

        .navbar-link:hover {
          background: #f1f5f9;
          color: #1e40af;
          border-color: #e0e7ff;
        }

        .navbar-link-active {
          background: #eff6ff;
          color: #1e40af;
          font-weight: 600;
          border-color: #3b82f6;
        }

        .navbar-link:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .navbar-link-active::after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 16px;
          right: 16px;
          height: 2px;
          background: #1e40af;
        }

        /* ============================================
           DESKTOP DROPDOWN MENU
           ============================================ */
        .navbar-menu-dropdown {
          position: relative;
          z-index: 1001;
        }

        .navbar-dropdown-toggle {
          padding: 10px 16px;
          color: #1e293b;
          text-decoration: none;
          font-weight: 500;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
          background: none;
          border: 2px solid transparent;
          font-size: 1rem;
          font-family: inherit;
          outline: none;
        }

        .navbar-dropdown-toggle:hover {
          background: #f1f5f9;
          color: #1e40af;
          border-color: #e0e7ff;
        }

        .navbar-dropdown-toggle-active {
          background: #eff6ff;
          color: #1e40af;
          font-weight: 600;
          border-color: #3b82f6;
        }

        .navbar-dropdown-toggle:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .navbar-dropdown-icon {
          font-size: 0.8rem;
          transition: transform 0.3s ease;
        }

        .navbar-menu-dropdown-open .navbar-dropdown-icon {
          transform: rotate(180deg);
        }

        .navbar-dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: #ffffff;
          border: 1px solid #e0e7ff;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.15);
          min-width: 220px;
          max-width: 280px;
          margin-top: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.3s ease;
          z-index: 1001;
          max-height: 400px;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .navbar-menu-dropdown-open .navbar-dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .navbar-dropdown-link {
          display: block;
          padding: 12px 16px;
          color: #1e293b;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s ease;
          border-bottom: 1px solid #f1f5f9;
        }

        .navbar-dropdown-link:last-child {
          border-bottom: none;
        }

        .navbar-dropdown-link:hover {
          background: #f1f5f9;
          color: #1e40af;
        }

        .navbar-dropdown-link-active {
          background: #eff6ff;
          color: #1e40af;
          font-weight: 600;
        }

        /* ============================================
           DESKTOP USER SECTION
           ============================================ */
        .navbar-user-section {
          display: flex;
          align-items: center;
          gap: 15px;
          overflow: visible;
        }

        .navbar-user-info {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          color: #1e293b;
        }

        .navbar-user-name {
          font-weight: 600;
          font-size: 0.95rem;
        }

        .navbar-user-role {
          font-size: 0.85rem;
          color: #64748b;
        }

        .navbar-logout-btn {
          padding: 10px 20px;
          background: #dc3545;
          color: white;
          border: 2px solid #dc3545;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
          font-family: inherit;
          outline: none;
          box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
        }

        .navbar-logout-btn:hover {
          background: #c82333;
          border-color: #c82333;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
        }

        .navbar-logout-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
        }

        .navbar-logout-btn:focus {
          outline: 2px solid #dc3545;
          outline-offset: 2px;
        }

        /* ============================================
           MOBILE TOGGLE BUTTON - HIDDEN ON DESKTOP
           ============================================ */
        .navbar-mobile-toggle {
          display: none;
          background: #ffffff;
          border: 2px solid #e0e7ff;
          font-size: 1.5rem;
          color: #1e40af;
          cursor: pointer;
          padding: 10px 12px;
          border-radius: 8px;
          transition: all 0.3s ease;
          font-family: inherit;
          outline: none;
          min-width: 44px;
          min-height: 44px;
          align-items: center;
          justify-content: center;
        }

        .navbar-mobile-toggle:hover {
          background: #f1f5f9;
          border-color: #3b82f6;
          transform: scale(1.05);
        }

        .navbar-mobile-toggle:active {
          transform: scale(0.95);
        }

        .navbar-mobile-toggle:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* ============================================
           MOBILE MENU - HIDDEN ON DESKTOP
           ============================================ */
        .navbar-mobile-menu {
          display: none;
          position: fixed;
          top: 70px;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ffffff;
          border-bottom: 2px solid #e0e7ff;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.15);
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
          z-index: 999;
        }

        .navbar-mobile-menu-open {
          display: block;
          max-height: calc(100vh - 70px);
          overflow: hidden;
        }

        .navbar-mobile-link {
          display: block;
          padding: 14px 20px;
          color: #1e293b;
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }

        .navbar-mobile-link:last-child {
          border-bottom: none;
        }

        .navbar-mobile-link:hover {
          background: #f1f5f9;
          color: #1e40af;
        }

        .navbar-mobile-link-active {
          background: #eff6ff;
          color: #1e40af;
          font-weight: 600;
          border-left: 4px solid #1e40af;
        }

        .navbar-mobile-dropdown {
          border-bottom: 1px solid #f1f5f9;
        }

        .navbar-mobile-dropdown-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          color: #1e293b;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          font-family: inherit;
          font-size: 1rem;
          outline: none;
        }

        .navbar-mobile-dropdown-toggle:hover {
          background: #f1f5f9;
          color: #1e40af;
        }

        .navbar-mobile-dropdown-toggle-active {
          background: #eff6ff;
          color: #1e40af;
          font-weight: 600;
        }

        .navbar-mobile-dropdown-toggle:focus {
          outline: 2px solid #3b82f6;
          outline-offset: -2px;
        }

        .navbar-mobile-dropdown-icon {
          font-size: 0.8rem;
          transition: transform 0.3s ease;
        }

        .navbar-mobile-dropdown-open .navbar-mobile-dropdown-icon {
          transform: rotate(180deg);
        }

        .navbar-mobile-dropdown-menu {
          background: #f8fafc;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }

        .navbar-mobile-dropdown-open .navbar-mobile-dropdown-menu {
          max-height: 400px;
          overflow: hidden;
        }

        .navbar-mobile-dropdown-link {
          display: block;
          padding: 12px 20px 12px 40px;
          color: #1e293b;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s ease;
          border-bottom: 1px solid #e2e8f0;
        }

        .navbar-mobile-dropdown-link:last-child {
          border-bottom: none;
        }

        .navbar-mobile-dropdown-link:hover {
          background: #f1f5f9;
          color: #1e40af;
        }

        .navbar-mobile-dropdown-link-active {
          background: #eff6ff;
          color: #1e40af;
          font-weight: 600;
          border-left: 4px solid #1e40af;
        }

        .navbar-mobile-user-section {
          padding: 15px 20px;
          border-top: 2px solid #e0e7ff;
          background: #f8fafc;
        }

        .navbar-mobile-user-info {
          margin-bottom: 12px;
        }

        .navbar-mobile-user-name {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .navbar-mobile-user-role {
          font-size: 0.85rem;
          color: #64748b;
        }

        .navbar-mobile-logout-btn {
          width: 100%;
          padding: 12px 20px;
          background: #dc3545;
          color: white;
          border: 2px solid #dc3545;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
          font-family: inherit;
          outline: none;
          box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
        }

        .navbar-mobile-logout-btn:hover {
          background: #c82333;
          border-color: #c82333;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
        }

        .navbar-mobile-logout-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
        }

        .navbar-mobile-logout-btn:focus {
          outline: 2px solid #dc3545;
          outline-offset: 2px;
        }

        /* ============================================
           RESPONSIVE BREAKPOINTS
           ============================================ */

        /* DESKTOP - 769px and above */
        @media (min-width: 769px) {
          .navbar-menu {
            display: flex !important;
          }

          .navbar-user-section {
            display: flex !important;
          }

          .navbar-mobile-toggle {
            display: none !important;
          }

          .navbar-mobile-menu {
            display: none !important;
          }
        }

        /* TABLET - 769px to 1024px */
        @media (max-width: 1024px) and (min-width: 769px) {
          .navbar-container {
            padding: 0 15px;
          }

          .navbar-link {
            padding: 10px 14px;
            font-size: 0.95rem;
          }

          .navbar-brand {
            font-size: 1.35rem;
          }

          .navbar-logout-btn {
            padding: 9px 18px;
            font-size: 0.9rem;
          }

          .navbar-dropdown-toggle {
            padding: 10px 14px;
            font-size: 0.95rem;
          }
        }

        /* MOBILE - 768px and below */
        @media (max-width: 768px) {
          .navbar-container {
            padding: 0 15px;
          }

          .navbar-content {
            height: 65px;
          }

          .navbar-menu {
            display: none !important;
          }

          .navbar-user-section {
            display: none !important;
          }

          .navbar-mobile-toggle {
            display: flex !important;
          }

          .navbar-mobile-menu {
            display: block;
            top: 65px;
          }

          .navbar-mobile-menu-open {
            max-height: calc(100vh - 65px);
          }

          .navbar-brand {
            font-size: 1.3rem;
          }
        }

        /* SMALL MOBILE - 480px and below */
        @media (max-width: 480px) {
          .navbar-container {
            padding: 0 12px;
            max-width: 100vw;
            overflow: hidden;
          }

          .navbar-content {
            max-width: 100%;
            overflow: hidden;
            height: 60px;
          }

          .navbar-mobile-menu {
            top: 60px;
          }

          .navbar-mobile-menu-open {
            max-height: calc(100vh - 60px);
          }

          .navbar-brand {
            font-size: 1.2rem;
          }

          .navbar-mobile-toggle {
            padding: 8px 10px;
            font-size: 1.3rem;
            min-width: 40px;
            min-height: 40px;
          }

          .navbar-mobile-link {
            padding: 14px 16px;
            font-size: 0.95rem;
          }

          .navbar-mobile-dropdown-toggle {
            padding: 14px 16px;
            font-size: 0.95rem;
          }

          .navbar-mobile-dropdown-link {
            padding: 12px 16px 12px 32px;
            font-size: 0.9rem;
          }

          .navbar-mobile-user-section {
            padding: 15px 16px;
          }

          .navbar-mobile-logout-btn {
            padding: 12px 16px;
            font-size: 0.95rem;
          }
        }

        /* EXTRA SMALL MOBILE - 360px and below */
        @media (max-width: 360px) {
          .navbar-container {
            padding: 0 10px;
            max-width: 100vw;
            overflow: hidden;
          }

          .navbar-content {
            max-width: 100%;
            overflow: hidden;
            height: 58px;
          }

          .navbar-brand {
            font-size: 1.1rem;
          }

          .navbar-mobile-menu {
            top: 58px;
          }

          .navbar-mobile-menu-open {
            max-height: calc(100vh - 58px);
          }

          .navbar-mobile-toggle {
            padding: 7px 9px;
            font-size: 1.2rem;
            min-width: 38px;
            min-height: 38px;
          }

          .navbar-mobile-link {
            padding: 12px 14px;
            font-size: 0.9rem;
          }

          .navbar-mobile-dropdown-toggle {
            padding: 12px 14px;
            font-size: 0.9rem;
          }

          .navbar-mobile-dropdown-link {
            padding: 10px 14px 10px 28px;
            font-size: 0.85rem;
          }
        }

        /* LARGE DESKTOP - 1400px and above */
        @media (min-width: 1400px) {
          .navbar-container {
            padding: 0 30px;
          }
        }
      `}</style>

      <nav className="navbar" role="navigation" aria-label="Main navigation">
        <div className="navbar-container">
          <div className="navbar-content">
            {/* Brand */}
            <NavLink to="/" className="navbar-brand">
              RTS Books
            </NavLink>

            {/* Desktop Menu */}
            <ul className="navbar-menu">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <NavLink
                    to={link.path}
                    className={({ isActive }) =>
                      `navbar-link ${isActive ? "navbar-link-active" : ""}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}

              {/* Desktop Dropdown Menu */}
              <li className={`navbar-menu-dropdown ${isMenuDropdownOpen ? "navbar-menu-dropdown-open" : ""}`}>
                <button
                  className={`navbar-dropdown-toggle ${
                    menuDropdownLinks.some((link) => link.path === location.pathname) ? "navbar-dropdown-toggle-active" : ""
                  }`}
                  onClick={toggleMenuDropdown}
                  aria-expanded={isMenuDropdownOpen}
                  aria-haspopup="true"
                >
                  Menu
                  <span className="navbar-dropdown-icon">▼</span>
                </button>
                <div className="navbar-dropdown-menu" role="menu">
                  {menuDropdownLinks.map((link) => (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      className={({ isActive }) =>
                        `navbar-dropdown-link ${isActive ? "navbar-dropdown-link-active" : ""}`
                      }
                      role="menuitem"
                      onClick={() => setIsMenuDropdownOpen(false)}
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </div>
              </li>
            </ul>

            {/* Desktop User Section */}
            <div className="navbar-user-section">
              {user && (
                <div className="navbar-user-info">
                  <div className="navbar-user-name">{user.name || "User"}</div>
                  <div className="navbar-user-role">{user.role || "User"}</div>
                </div>
              )}
              <button className="navbar-logout-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>

            {/* Mobile Toggle */}
            <button
              className="navbar-mobile-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? "✕" : "☰"}
            </button>
          </div>

          {/* Mobile Menu */}
          <div
            className={`navbar-mobile-menu ${isMobileMenuOpen ? "navbar-mobile-menu-open" : ""}`}
            role="menu"
          >
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `navbar-mobile-link ${isActive ? "navbar-mobile-link-active" : ""}`
                }
                role="menuitem"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            ))}

            {/* Mobile Dropdown Menu */}
            <div
              className={`navbar-mobile-dropdown ${
                isMobileMenuDropdownOpen ? "navbar-mobile-dropdown-open" : ""
              }`}
            >
              <button
                className={`navbar-mobile-dropdown-toggle ${
                  menuDropdownLinks.some((link) => link.path === location.pathname) ? "navbar-mobile-dropdown-toggle-active" : ""
                }`}
                onClick={toggleMobileMenuDropdown}
                aria-expanded={isMobileMenuDropdownOpen}
                aria-haspopup="true"
              >
                Menu
                <span className="navbar-mobile-dropdown-icon">▼</span>
              </button>
              <div className="navbar-mobile-dropdown-menu" role="menu">
                {menuDropdownLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `navbar-mobile-dropdown-link ${isActive ? "navbar-mobile-dropdown-link-active" : ""}`
                    }
                    role="menuitem"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setIsMobileMenuDropdownOpen(false);
                    }}
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            </div>

            {/* Mobile User Section */}
            <div className="navbar-mobile-user-section">
              {user && (
                <div className="navbar-mobile-user-info">
                  <div className="navbar-mobile-user-name">{user.name || "User"}</div>
                  <div className="navbar-mobile-user-role">{user.role || "User"}</div>
                </div>
              )}
              <button
                className="navbar-mobile-logout-btn"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;

